import React, { useState, useEffect, useRef } from 'react';
import { auth } from '../../firebase/firebaseConfig';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  resolveMarketingBackUrl,
} from '../../utils/authRoutes';
import { useUserProfilePrefetch } from '../../hooks/useUserProfile';
import {
  validatePasswordStrength,
  calculatePasswordStrength,
  getPasswordStrengthLabel
} from '../../utils/passwordValidation';
import { getOrCreateCsrfToken } from '../../utils/csrfProtection';
import useErrorHandler from '../../hooks/useErrorHandler';
import { ErrorCategory } from '../../utils/errorHandling/errorTypes';
import errorService from '../../utils/errorHandling';
import { ErrorMessage } from '../ErrorHandling';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  sendEmailVerification,
  getAdditionalUserInfo
} from 'firebase/auth';
import { trackSignUp, trackSignupCompleted } from '../../utils/analytics';
import { ReactComponent as FlashCodeIcon } from '../../Icons/flashcode-icon_2.svg';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import './Auth.css';
import { ensureOnboardingCookbook } from '../../utils/onboarding';
import { checkRateLimit, recordAttempt, getTimeLeftMessage } from '../../utils/rateLimiter';
import { validateEmail, getEmailValidationState } from '../../utils/emailValidation';
import { validateEmailWithAPI, isAPIConfigured } from '../../utils/emailValidationAPI';
import { mergeAnonymousAccount } from '../../utils/accountMerge';
import { clearGuestStudyState } from '../../utils/demoState';
import { isFirebaseAuthCancellation } from '../../utils/errorSanitizer';
import { beginGooglePopupFlow } from '../../utils/googlePopupFocusReset';
import { ROADMAP_PATHS } from '../Roadmap/roadmapPaths';
import {
  captureLandingIntent,
  clearLandingIntent,
  getPostAuthRedirectPath,
} from '../../utils/landingIntent';

const isSignUpFromSearchParams = (searchParams) => {
  const signup = searchParams.get('signup');
  const mode = (searchParams.get('mode') || '').toLowerCase();

  if (mode === 'signup' || signup === 'true' || signup === '1') {
    return true;
  }

  if (mode === 'signin' || mode === 'login') {
    return false;
  }

  return null;
};

const Auth = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(() => isSignUpFromSearchParams(searchParams) ?? false);
  const [error, setError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isHandoffBusy, setIsHandoffBusy] = useState(false);
  const isAuthBusy = isLoading || isHandoffBusy;
  const [emailValidation, setEmailValidation] = useState({ isValid: null, message: '', showWarning: false });
  const [rateLimitError, setRateLimitError] = useState('');
  const { prefetchUserProfile } = useUserProfilePrefetch();
  const onboardingSnippetCreated = useRef(false);
  const authStateChangeHandled = useRef(false);
  const googleFlowRef = useRef(null);
  /** Synchronous guard — blocks double-clicks before React re-renders disabled state. */
  const authInFlightRef = useRef(false);

  const beginAuthAttempt = () => {
    if (authInFlightRef.current) return false;
    authInFlightRef.current = true;
    setIsLoading(true);
    return true;
  };

  const beginAuthHandoff = () => {
    authInFlightRef.current = true;
    setIsHandoffBusy(true);
    setIsLoading(true);
  };

  const releaseAuthAttempt = () => {
    authInFlightRef.current = false;
    setIsHandoffBusy(false);
    setIsLoading(false);
  };
  // True when the user arrived here from the guest soft-gate banner or
  // alternative deep links that want the anon UID merged into the new
  // account instead of replaced. Drives the merge-aware sign-in/up paths.
  const fromDemo = (searchParams.get('from') || '').toLowerCase() === 'demo';
  const marketingBackUrl = resolveMarketingBackUrl(searchParams);

  const setAuthMode = (signUp) => {
    setIsSignUp(signUp);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (signUp) {
          next.set('mode', 'signup');
        } else {
          next.delete('mode');
          next.delete('signup');
        }
        return next;
      },
      { replace: true }
    );
  };

  const { handleError, executeWithErrorHandling } = useErrorHandler({
    category: ErrorCategory.AUTHENTICATION,
    onError: (message) => setError(message)
  });

  const handleSuccessfulAuth = async (user) => {
    if (authStateChangeHandled.current) return;
    authStateChangeHandled.current = true;
    beginAuthHandoff();

    try {
      const isPasswordProvider = user.providerData.some(
        (provider) => provider.providerId === 'password'
      );

      if (isPasswordProvider && !user.emailVerified) {
        navigate('/verify-email');
        return;
      }

      localStorage.setItem('app_loading', 'true');

      await Promise.all([
        prefetchUserProfile(user.uid).catch((err) => {
          console.warn('Prefetch user profile failed (non-blocking):', err);
          errorService.handleError(err, {
            category: ErrorCategory.AUTHENTICATION,
            context: { step: 'postAuthPrefetch', source: 'Auth.prefetchUserProfile', userId: user?.uid }
          });
        }),
        (async () => {
          try {
            if (!onboardingSnippetCreated.current) {
              // Skip for anonymous guests. Registered empty accounts still
              // get a welcome cookbook; guests who already studied a track
              // already have decks so this short-circuits after merge.
              await ensureOnboardingCookbook(user);
              onboardingSnippetCreated.current = true;
            }
          } catch (err) {
            console.warn('Onboarding failed (non-blocking):', err);
            errorService.handleError(err, {
              category: ErrorCategory.AUTHENTICATION,
              context: { step: 'onboarding', source: 'Auth.onboarding', userId: user?.uid }
            });
          }
        })()
      ]);

      if (!user.isAnonymous) {
        clearGuestStudyState();
      }

      localStorage.setItem(`${user.uid}_needsRefresh`, 'true');
      localStorage.setItem(`${user.uid}_lastPage`, 'Home');
      localStorage.setItem(`${user.uid}_selectedPage`, null);

      const intentPath = getPostAuthRedirectPath();
      navigate(intentPath || '/home');
      clearLandingIntent();
    } catch (authErr) {
      console.error('Error during authentication:', authErr);
      errorService.handleError(authErr, {
        category: ErrorCategory.AUTHENTICATION,
        context: { step: 'handleSuccessfulAuth', source: 'Auth.handleSuccessfulAuth' }
      });
      authStateChangeHandled.current = false;
      releaseAuthAttempt();
    } finally {
      localStorage.removeItem('app_loading');
    }
  };

  const resetAuthState = () => {
    authStateChangeHandled.current = false;
    onboardingSnippetCreated.current = false;
    localStorage.removeItem('app_loading');
    releaseAuthAttempt();
  };

  useEffect(() => {
    captureLandingIntent(window.location.search);
  }, [searchParams]);

  useEffect(() => {
    const resolved = isSignUpFromSearchParams(searchParams);
    if (resolved !== null) {
      setIsSignUp(resolved);
      return;
    }
    if (!searchParams.get('mode') && !searchParams.get('signup')) {
      setIsSignUp(false);
    }
  }, [searchParams]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        if (user.isAnonymous) {
          releaseAuthAttempt();
          return;
        }
        await handleSuccessfulAuth(user);
      } else {
        resetAuthState();
      }
    });

    return () => {
      unsubscribe();
      resetAuthState();
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!beginAuthAttempt()) return;
    setError('');
    setRateLimitError('');

    try {
      let emailValidationResult;
      if (isAPIConfigured()) {
        emailValidationResult = await validateEmailWithAPI(email, {
          useAPI: true,
          checkProfanity: true
        });
      } else {
        emailValidationResult = validateEmail(email);
      }

      if (!emailValidationResult.valid) {
        setError(emailValidationResult.error);
        releaseAuthAttempt();
        return;
      }

      if (isSignUp) {
        const rateLimitCheck = checkRateLimit('signups');
        if (!rateLimitCheck.allowed) {
          setRateLimitError(
            `Too many signup attempts. Please wait ${getTimeLeftMessage(rateLimitCheck.timeLeft)} before trying again.`
          );
          releaseAuthAttempt();
          return;
        }

        const validation = validatePasswordStrength(password);
        if (!validation.isValid) {
          setError(validation.message);
          releaseAuthAttempt();
          return;
        }

        // Demo-aware path: if the user is currently an anon Firebase user
        // (typical when arriving from the soft-gate banner), link their
        // email credential to the existing UID so all guest data — decks,
        // EXP, schedule, streak — carries over without copy/move.
        const currentlyAnon =
          auth.currentUser && auth.currentUser.isAnonymous;
        let newUser;
        if (currentlyAnon) {
          const result = await mergeAnonymousAccount({
            provider: 'password',
            email,
            password,
          });
          newUser = result.user;
        } else {
          const userCredential = await createUserWithEmailAndPassword(auth, email, password);
          newUser = userCredential.user;
        }

        recordAttempt('signups');
        trackSignUp('email');
        trackSignupCompleted({
          method: 'email',
          surface: currentlyAnon
            ? fromDemo
              ? 'auth-page-demo-merge'
              : 'auth-page-guest-merge'
            : 'auth-page',
        });

        try {
          await sendEmailVerification(newUser, {
            url: window.location.origin + '/home',
            handleCodeInApp: false
          });
        } catch (verificationError) {
          console.error('Error sending verification email:', verificationError);
          errorService.handleError(verificationError, {
            category: ErrorCategory.AUTHENTICATION,
            context: { step: 'sendEmailVerification', email: newUser?.email ? '[redacted]' : null }
          });
          setError(
            "We couldn't send the verification email right now. You'll be redirected to the verification page where you can request a new one."
          );
          beginAuthHandoff();
          return;
        }
      } else {
        // Demo-aware path: if a returning visitor is currently anon and
        // arrived via the soft-gate, route the sign-in through mergeAnonymousAccount
        // so the anon UID's data carries over to the existing account.
        const currentlyAnon =
          auth.currentUser && auth.currentUser.isAnonymous;
        if (currentlyAnon) {
          await mergeAnonymousAccount({ provider: 'password', email, password });
        } else {
          await signInWithEmailAndPassword(auth, email, password);
        }
      }
    } catch (submitErr) {
      handleError(submitErr);
      releaseAuthAttempt();
    }
  };

  const handleGoogleSignIn = async () => {
    if (!beginAuthAttempt()) return;

    const releaseGoogleLoadingUnlessSignedIn = () => {
      if (authInFlightRef.current) return;
      if (auth.currentUser && !auth.currentUser.isAnonymous) return;
      releaseAuthAttempt();
    };

    const { endGooglePopupFlow, isActiveFlow } = beginGooglePopupFlow(
      googleFlowRef,
      releaseGoogleLoadingUnlessSignedIn
    );

    try {
      const currentlyAnon = auth.currentUser && auth.currentUser.isAnonymous;
      if (currentlyAnon) {
        await mergeAnonymousAccount({ provider: 'google' });
        beginAuthHandoff();
        endGooglePopupFlow();
        trackSignUp('google');
        trackSignupCompleted({
          method: 'google',
          surface: fromDemo ? 'auth-page-demo-merge' : 'auth-page-guest-merge',
        });
        return;
      }

      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      beginAuthHandoff();
      endGooglePopupFlow();
      // getAdditionalUserInfo tells us if Firebase just created the account
      // (first Google sign-in for this email) vs returning login — only the
      // former should count as a sign_up conversion in GA4.
      const additional = getAdditionalUserInfo(result);
      if (additional?.isNewUser) {
        trackSignUp('google');
        trackSignupCompleted({ method: 'google', surface: 'auth-page' });
      }
    } catch (googleErr) {
      endGooglePopupFlow();
      if (!isActiveFlow()) return;
      if (isFirebaseAuthCancellation(googleErr)) {
        releaseAuthAttempt();
        return;
      }
      handleError(googleErr);
      releaseAuthAttempt();
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Please enter your email address first');
      return;
    }

    const emailValidationResult = validateEmail(email);
    if (!emailValidationResult.valid) {
      setError(emailValidationResult.error);
      return;
    }

    const rateLimitCheck = checkRateLimit('passwordResets');
    if (!rateLimitCheck.allowed) {
      setError(
        `Too many password reset attempts. Please wait ${getTimeLeftMessage(rateLimitCheck.timeLeft)} before trying again.`
      );
      return;
    }

    setIsLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      recordAttempt('passwordResets');
      setError('Password reset email sent! Check your inbox.');
    } catch (resetErr) {
      handleError(resetErr);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    executeWithErrorHandling(
      async () => {
        await getOrCreateCsrfToken();
      },
      [],
      {
        category: ErrorCategory.SECURITY,
        context: { operation: 'initializeCsrfToken' }
      }
    );
  }, [executeWithErrorHandling]);

  useEffect(() => {
    if (password) {
      const strength = calculatePasswordStrength(password);
      setPasswordStrength(strength);

    } else {
      setPasswordStrength(0);
    }
  }, [password]);

  useEffect(() => {
    if (email) {
      const validationState = getEmailValidationState(email);
      setEmailValidation(validationState);
    } else {
      setEmailValidation({ isValid: null, message: '', showWarning: false });
    }
  }, [email]);

  const emailInputClass =
    'flashcode-auth-page__input' +
    (emailValidation.isValid === false ? ' flashcode-auth-page__input--error' : '') +
    (emailValidation.isValid === true ? ' flashcode-auth-page__input--valid' : '');

  const strengthColor =
    passwordStrength < 30
      ? '#ff4d4d'
      : passwordStrength < 50
        ? '#ffaa00'
        : passwordStrength < 70
          ? '#ffdd00'
          : passwordStrength < 90
            ? '#00cc44'
            : '#00aa88';

  return (
    <div className="flashcode-auth-page">
      <div className="flashcode-auth-page__card flashcode-auth-page__fade-up">
        <aside className="flashcode-auth-page__brand">
          <div className="flashcode-auth-page__brand-grid" aria-hidden />
          <div className="flashcode-auth-page__brand-glow" aria-hidden />
          <div className="flashcode-auth-page__logo">
            <span className="flashcode-auth-page__logo-mark" aria-hidden>
              <FlashCodeIcon />
            </span>
            <span className="flashcode-auth-page__logo-name">FlashCode</span>
          </div>

          <div className="flashcode-auth-page__hero">
            <div className="flashcode-auth-page__hero-copy">
              <strong className="flashcode-auth-page__hero-headline">
                Learn and remember
                <br />
                how to code.
              </strong>
              5 minutes a day.
              <br />
              Spaced repetition.
              <br />
              No excuses.
            </div>
          </div>

          <div className="flashcode-auth-page__tagline">Stop forgetting syntax.</div>
        </aside>

        <div className="flashcode-auth-page__form-column">
          <div className="flashcode-auth-page__form-grid" aria-hidden />
          <div
            className={`flashcode-auth-page__form-inner${isAuthBusy ? ' flashcode-auth-page__form-inner--busy' : ''}`}
            aria-busy={isAuthBusy}
          >
            {marketingBackUrl && (
              <button
                type="button"
                className="flashcode-auth-page__back-link"
                onClick={() => {
                  window.location.assign(marketingBackUrl);
                }}
                disabled={isAuthBusy}
              >
                ← Back to FlashCode
              </button>
            )}

            <h2 className="flashcode-auth-page__form-title">
              {isSignUp ? 'Create your account.' : 'Welcome back.'}
            </h2>
            <p className="flashcode-auth-page__form-subtitle">
              {isSignUp ? 'Your streak starts today.' : 'Pick up where you left off.'}
            </p>

            <div className="flashcode-auth-page__messages">
              {error && <ErrorMessage message={error} type="error" />}
              {rateLimitError && <ErrorMessage message={rateLimitError} type="error" />}
            </div>

            <div className="flashcode-auth-page__mode-tabs">
              <button
                type="button"
                className={`flashcode-auth-page__mode-tab${!isSignUp ? ' flashcode-auth-page__mode-tab--active' : ''}`}
                onClick={() => setAuthMode(false)}
                disabled={isAuthBusy}
              >
                Sign In
              </button>
              <button
                type="button"
                className={`flashcode-auth-page__mode-tab${isSignUp ? ' flashcode-auth-page__mode-tab--active' : ''}`}
                onClick={() => setAuthMode(true)}
                disabled={isAuthBusy}
              >
                Sign Up
              </button>
            </div>

            <button
              type="button"
              className={`flashcode-auth-page__btn-google${isAuthBusy ? ' flashcode-auth-page__btn-google--loading' : ''}`}
              onClick={handleGoogleSignIn}
              disabled={isAuthBusy}
            >
              <span className="flashcode-auth-page__btn-google-icon" aria-hidden>
                <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
                  <path
                    d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908C16.658 14.015 17.64 11.707 17.64 9.2z"
                    fill="#4285F4"
                  />
                  <path
                    d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"
                    fill="#34A853"
                  />
                  <path
                    d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
                    fill="#EA4335"
                  />
                </svg>
              </span>
              <span className="flashcode-auth-page__btn-google-label">
                {isSignUp ? 'Sign up with Google' : 'Continue with Google'}
              </span>
            </button>


            <div className="flashcode-auth-page__divider">
              <div className="flashcode-auth-page__divider-line" aria-hidden />
              <span className="flashcode-auth-page__divider-text">or use email</span>
              <div className="flashcode-auth-page__divider-line" aria-hidden />
            </div>

            <form onSubmit={handleSubmit} noValidate>
              <div className="flashcode-auth-page__form-fields">
                <div>
                  <label htmlFor="flashcode-auth-email" className="flashcode-auth-page__field-label">
                    Email
                  </label>
                  <input
                    id="flashcode-auth-email"
                    type="email"
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isAuthBusy}
                    className={emailInputClass}
                    placeholder="you@example.com"
                  />
                  {emailValidation.isValid === false && (
                    <span className="flashcode-auth-page__email-hint flashcode-auth-page__email-hint--error">
                      {emailValidation.message}
                    </span>
                  )}
                  {emailValidation.showWarning && emailValidation.isValid === true && (
                    <span className="flashcode-auth-page__email-hint flashcode-auth-page__email-hint--warn">
                      {emailValidation.message}
                    </span>
                  )}
                </div>

                <div>
                  <div className="flashcode-auth-page__field-label-row">
                    <label htmlFor="flashcode-auth-password" className="flashcode-auth-page__field-label">
                      Password
                    </label>
                    {!isSignUp && (
                      <button
                        type="button"
                        className="flashcode-auth-page__forgot-btn"
                        onClick={handleForgotPassword}
                        disabled={isAuthBusy}
                      >
                        Forgot password?
                      </button>
                    )}
                  </div>
                  <div className="flashcode-auth-page__password-wrap">
                    <input
                      id="flashcode-auth-password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete={isSignUp ? 'new-password' : 'current-password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onFocus={() => isSignUp && setShowPasswordRequirements(true)}
                      required
                      disabled={isAuthBusy}
                      className="flashcode-auth-page__input flashcode-auth-page__input--password"
                      placeholder={isSignUp ? 'Min. 8 characters' : '••••••••'}
                    />
                    <button
                      type="button"
                      className="flashcode-auth-page__password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                      disabled={isAuthBusy}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>

                  {isSignUp && password && (
                    <div className="flashcode-auth-page__password-strength">
                      <div className="flashcode-auth-page__password-meter">
                        <div
                          className="flashcode-auth-page__password-meter-fill"
                          style={{
                            width: `${passwordStrength}%`,
                            backgroundColor: strengthColor
                          }}
                        />
                      </div>
                      <span className="flashcode-auth-page__password-meter-label">
                        {getPasswordStrengthLabel(passwordStrength)}
                      </span>
                    </div>
                  )}

                  {isSignUp && showPasswordRequirements && (
                    <div className="flashcode-auth-page__password-requirements">
                      <h4>Password must contain</h4>
                      <ul>
                        <li className={password.length >= 8 ? 'requirement-met' : ''}>At least 8 characters</li>
                        <li className={/[A-Z]/.test(password) ? 'requirement-met' : ''}>At least one uppercase letter</li>
                        <li className={/[a-z]/.test(password) ? 'requirement-met' : ''}>At least one lowercase letter</li>
                        <li className={/\d/.test(password) ? 'requirement-met' : ''}>At least one number</li>
                        <li
                          className={
                            /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password) ? 'requirement-met' : ''
                          }
                        >
                          At least one special character
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              {isSignUp && (
                <p className="flashcode-auth-page__terms">
                  By signing up you agree to our <span className="flashcode-auth-page__terms-link">Terms</span> and{' '}
                  <span className="flashcode-auth-page__terms-link">Privacy Policy</span>.
                </p>
              )}

              <button
                type="submit"
                className={`flashcode-auth-page__btn-primary${isAuthBusy ? ' flashcode-auth-page__btn-primary--loading' : ''}`}
                disabled={isAuthBusy}
              >
                {!isAuthBusy && (isSignUp ? 'Start training →' : 'Sign in →')}
              </button>
            </form>

            <p className="flashcode-auth-page__footer-toggle">
              {isSignUp ? 'Already training? ' : 'New here? '}
              <button
                type="button"
                className="flashcode-auth-page__footer-link"
                onClick={() => setAuthMode(!isSignUp)}
                disabled={isAuthBusy}
              >
                {isSignUp ? 'Sign in' : 'Create account'}
              </button>
            </p>

            <p className="flashcode-auth-page__guest-row">
              <button
                type="button"
                className="flashcode-auth-page__guest-link"
                onClick={() => navigate(ROADMAP_PATHS.menu, { replace: true })}
                disabled={isAuthBusy}
              >
                Continue without signing in
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
